import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import UnitLinkPicker from 'components/UnitLinkField/components/UnitLinkPicker';
import {
  AIR, ARCTIC_TRUCK, COMMENT_MANUFACTURE, DISTANCE, FROM_NAME, FROM_TYPE, FROM_UNIT_ID,
  MANUFACTURE_CONNECTION_TYPE, MONETARY_VOLUMES, QUANTITY, RAIL, RIGID_TRUCK, SEA,
  SOURCING_CONNECTIONS, TRUCK, UNIT_CURRENCY, UNIT_MONETARY_VOLUMES, VAN,
} from 'constants/formFields';
import { VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useUnitRefs } from 'contexts/UnitRefsContext';
import useAnchoredDropdown from 'hooks/useAnchoredDropdown';
import { getSourcingKind, SOURCING_KINDS } from 'utils/sourcingConnections';

import SourcingConnectionItem from './components/SourcingConnectionItem';

import type IUnitLinkItem from 'components/UnitLinkField/interfaces/IUnitLinkItem';
import type IUnitRef from 'utils/interfaces/IUnitRef';
import type { TSourcingKind, TSourcingRow } from 'utils/sourcingConnections';
import type { TCreateProductSchema } from 'utils/validators';
import type ISourcingConnections from './interfaces/ISourcingConnections';

import styles from './sass/SourcingConnections.module.scss';

// Both record types a product may be sourced from. They are offered in one list because the user
// is answering one question — where does this come from — and the kind of connection that answer
// implies is the backend's to decide, not something the user picks.
const DEFAULT_SOURCE_TYPES = [
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
  VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
];

// A row is identified by its source unit and its kind together: the same unit may legitimately
// have an edge in both connection tables on a map seeded before factory routing existed, and
// keying on the unit alone would collide.
const buildRowKey = (kind: string, unitId: number | null) => `${kind}-${unitId}`;

// A row only fills in the half of the shape belonging to its kind, so the schema's inferred type
// — which describes both halves at once — is wider than any single row. The form is read through
// the same boundary in reverse, so the cast sits on both sides of it rather than only this one.
type TFormSourcingRows = NonNullable<TCreateProductSchema[typeof SOURCING_CONNECTIONS]>;

function SourcingConnections({
  control,
  watch,
  setValue,
  enumData,
  currentUnitId,
  sourceTypes = DEFAULT_SOURCE_TYPES,
  profitCentreUIDs,
  label,
  boundarySelector,
}: Readonly<ISourcingConnections>) {
  const { t } = useTranslation();
  const { getRefs } = useUnitRefs();

  const watchedConnections = watch(SOURCING_CONNECTIONS);
  // Held behind a memo so the empty-array fallback does not produce a new reference each
  // render, which would invalidate every derived memo below it.
  const connections = useMemo(
    () => (watchedConnections || []) as TSourcingRow[],
    [watchedConnections],
  );

  const {
    triggerRef, dropdownRef, isOpen, position, searchTerm, setSearchTerm, toggle, close,
  } = useAnchoredDropdown({ boundarySelector });

  // Scoping is applied here rather than through `getRefs`, which narrows to a single profit
  // centre: a record can sit in several, and every one of them contributes sources.
  const refs = useMemo(() => {
    const allRefs = sourceTypes.flatMap((type) => getRefs(type));

    if (!profitCentreUIDs) {
      return allRefs;
    }

    return allRefs.filter(
      (ref) => ref.profitCentreUIDs.some((uid) => profitCentreUIDs.includes(uid)),
    );
  }, [getRefs, profitCentreUIDs, sourceTypes]);

  // A card cannot supply itself, and a unit with no id cannot be addressed as a source.
  const selectableRefs = useMemo(
    () => refs.filter((ref) => ref.unitId && ref.unitId !== currentUnitId),
    [currentUnitId, refs],
  );

  // The picker offers one entry per source, keyed by the kind that source would produce, so
  // picking it toggles the row it would actually create.
  const options = useMemo<IUnitLinkItem[]>(() => selectableRefs.map((ref) => ({
    itemKey: buildRowKey(getSourcingKind(ref), ref.unitId),
    name: ref.label,
    type: ref.type,
  })), [selectableRefs]);

  const filteredOptions = useMemo(() => {
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();

    if (!normalizedSearchTerm) {
      return options;
    }

    return options.filter((option) => option.name.toLowerCase().includes(normalizedSearchTerm));
  }, [options, searchTerm]);

  const selectedKeys = useMemo(
    () => new Set(connections.map((connection) => buildRowKey(
      connection.kind,
      Number(connection[FROM_UNIT_ID]),
    ))),
    [connections],
  );

  const refByKey = useMemo(
    () => new Map(selectableRefs.map((ref) => [buildRowKey(getSourcingKind(ref), ref.unitId), ref])),
    [selectableRefs],
  );

  const optionByKey = useMemo(
    () => new Map(options.map((option) => [option.itemKey, option])),
    [options],
  );

  const handleRemove = (index: number) => {
    setValue(
      SOURCING_CONNECTIONS,
      connections.filter((_, itemIndex) => itemIndex !== index) as TFormSourcingRows,
      { shouldDirty: true },
    );
  };

  // Only the half of the row belonging to its kind is filled in. The other half stays empty and
  // is never read: the split drops it, and the card never renders those controls.
  const buildRow = (ref: IUnitRef, kind: TSourcingKind): TSourcingRow => {
    const identity = {
      kind,
      // No `id`: the backend reads its absence as "create this edge".
      [FROM_UNIT_ID]: ref.unitId as number,
      [FROM_NAME]: ref.label,
      [FROM_TYPE]: ref.type,
    };

    if (kind === SOURCING_KINDS.MANUFACTURE) {
      return {
        ...identity,
        [MANUFACTURE_CONNECTION_TYPE]: {},
        [COMMENT_MANUFACTURE]: '',
      };
    }

    return {
      ...identity,
      [AIR]: false,
      [SEA]: false,
      [RAIL]: false,
      [VAN]: false,
      [TRUCK]: false,
      [RIGID_TRUCK]: false,
      [ARCTIC_TRUCK]: false,
      [DISTANCE]: '',
      [MONETARY_VOLUMES]: '',
      [UNIT_MONETARY_VOLUMES]: {},
      [QUANTITY]: '',
      [UNIT_CURRENCY]: {},
    };
  };

  const handleToggle = (item: IUnitLinkItem) => {
    const existingIndex = connections.findIndex(
      (connection) => buildRowKey(connection.kind, Number(connection[FROM_UNIT_ID])) === item.itemKey,
    );

    if (existingIndex !== -1) {
      handleRemove(existingIndex);
      return;
    }

    const ref = refByKey.get(item.itemKey);

    // A ref with no board unit has nothing to address as a source, so it cannot be connected.
    // The options already exclude those, so this only re-narrows the type.
    if (!ref?.unitId) {
      return;
    }

    setValue(
      SOURCING_CONNECTIONS,
      [...connections, buildRow(ref, getSourcingKind(ref))] as TFormSourcingRows,
      { shouldDirty: true },
    );
    // One pick per opening: the new section needs filling in before the next is useful.
    close();
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.label}>{label || t('products.fields.suppliedBy')}</span>
        <button
          type="button"
          className={styles.addButton}
          ref={triggerRef}
          onClick={toggle}
          aria-label={t('products.addSupplierConnection')}
          aria-expanded={isOpen}
        >
          <PlusIcon />
        </button>
      </div>

      {connections.length ? connections.map((connection, index) => {
        const rowKey = buildRowKey(connection.kind, Number(connection[FROM_UNIT_ID]));
        // The picker only lists this map's units, so a connection whose source sits outside it
        // still has to render — fall back to the name the record GET supplied.
        const from = optionByKey.get(rowKey) || {
          itemKey: rowKey,
          name: connection[FROM_NAME] || `${connection[FROM_UNIT_ID]}`,
          type: connection[FROM_TYPE] || VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
        };

        return (
          <SourcingConnectionItem
            key={rowKey}
            control={control}
            index={index}
            kind={connection.kind}
            enumData={enumData}
            from={from}
            onRemove={() => handleRemove(index)}
          />
        );
      }) : (
        <p className={styles.emptyState}>
          {/* Without this, a scoped picker with nothing in scope just opens empty, and the
              reason — no profit centres on the record yet — is invisible. */}
          {options.length
            ? t('products.noSupplierConnections')
            : t('products.noSourcesInScope')}
        </p>
      )}

      <UnitLinkPicker
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        position={position}
        searchTerm={searchTerm}
        onSearchTermChange={(event) => setSearchTerm(event.target.value)}
        options={filteredOptions}
        selectedKeys={selectedKeys}
        onOptionToggle={handleToggle}
      />
    </div>
  );
}

export default SourcingConnections;
